import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Reveal, RevealGroup, Label, HEADING } from "@/components/site/Reveal";
import { PageSection } from "@/components/site/PageLayout";

export type FaqItem = { q: string; a: string };

/**
 * FAQ accordion. Pair with `faqSchema(items)` in the route's head() for
 * FAQPage JSON-LD.
 */
export function FaqSection({
  label = "FAQ",
  title,
  items,
  theme = "default",
}: {
  label?: string;
  title?: React.ReactNode;
  items: FaqItem[];
  theme?: "default" | "surface";
}) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <PageSection theme={theme}>
      <div className="grid gap-16 lg:grid-cols-[1fr_1.6fr]">
        <div>
          <RevealGroup stagger={70} maxStagger={200}>
            <Label n="+" text={label} />
            <h2 className={`mt-6 ${HEADING.h2}`}>
              {title ?? (
                <>
                  Frequently asked <span className="text-sun">questions.</span>
                </>
              )}
            </h2>
            <p className="mt-6 text-muted-foreground">Have a different question?</p>
            <Link
              to="/contact"
              className="mt-2 inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sun hover:text-foreground transition-colors"
            >
              Contact us <span>→</span>
            </Link>
          </RevealGroup>
        </div>
        <Reveal>
          <div className="divide-y divide-border border-y border-border">
            {items.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.q}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-6 py-6 text-left transition-colors hover:text-sun"
                  >
                    <span className="font-display text-lg md:text-xl font-bold uppercase">
                      {f.q}
                    </span>
                    <span
                      className={`shrink-0 font-mono text-2xl text-sun transition-transform ${isOpen ? "rotate-45" : ""}`}
                    >
                      +
                    </span>
                  </button>
                  <div
                    className="overflow-hidden text-muted-foreground transition-all"
                    style={{
                      maxHeight: isOpen ? 500 : 0,
                      opacity: isOpen ? 1 : 0,
                      paddingBottom: isOpen ? 24 : 0,
                    }}
                  >
                    <p className="max-w-2xl leading-relaxed">{f.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </PageSection>
  );
}
